import {
  Body,
  BodyProps,
  Box,
  BoxProps,
  ButtCon,
  Button,
  Card,
  Heading,
  Icon,
  ProgressBar,
  ResponsiveSizes,
  TextInput,
  Tooltip
} from '@biom3/react';
import { StockStatusEnum } from 'lib/woocomerce/__generated__/graphql';
import { Product, ProductCollectionType } from 'lib/woocomerce/types';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { CardFrame } from 'src/components/product-card/card-frame';
import { useProductsContext } from 'src/context/products-context';
import { useStore } from 'src/context/store-context';
import { useConfirmModal } from 'src/hooks/use-confirm-modal';
import { useConnectWidget } from 'src/hooks/use-connect-widget';
import { useQuantity } from 'src/hooks/use-quantity';
import { toFormatedCurrency } from 'src/utils/misc';
import {
  gGoG2GoGApi,
  gGoGBalanceApi,
  getCurrentUserGogBalance,
  getGGoGWithdrawalFeeGog
} from '../../api/g-gog-api';
import { getProductAttributes } from '../../utils/product-attributes';
import CountdownTimer from '../count-down-timer';
import styles from './product-card.module.css';

function cutDecimal(num: number, n: number) {
  const multiple = Math.pow(10, n);
  return Math.floor(num * multiple) / multiple;
}

export type ProductCardProps = {
  product: Product;
  onClick: (product: Product, qty: number, envKey?: 'default' | 'gog') => void; // eslint-disable-line no-unused-vars
};

const StockStatus = ({ stockQty, product }: { product: Product; stockQty: number }) => {
  const [{ saleEnded }] = useStore();

  const unlimitedStock =
    product.stockQuantity === null && product.stockStatus === StockStatusEnum.InStock;
  const stockLabelA = unlimitedStock ? 'In stock' : `${stockQty} remaining`;
  const stockLabelB = stockQty > 0 ? stockLabelA : 'Out of stock';
  const label = saleEnded ? 'sale ended' : stockLabelB;

  return (
    <Box
      sx={{
        borderRadius: 'base.spacing.x1',
        backgroundColor: 'base.color.translucent.emphasis.300',
        px: 'base.spacing.x4',
        py: 'base.spacing.x2',
        userSelect: 'none'
      }}
    >
      {label}
    </Box>
  );
};

const NotTradableTag = () => {
  return (
    <Tooltip>
      <Tooltip.Target>
        <Body
          size="small"
          weight="bold"
          sx={{
            px: 'base.spacing.x1',
            brad: 'base.borderRadius.x1',
            color: 'base.color.text.body.inverse.primary',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            userSelect: 'none',
            bg: 'base.color.accent.8',
            '&:hover': {
              filter: 'brightness(0.9)'
            }
          }}
        >
          Not tradable
          <Icon
            icon="InformationCircle"
            sx={{
              ml: 'base.spacing.x1',
              fill: 'inherit',
              width: 'base.spacing.x4',
              height: 'base.spacing.x4'
            }}
          />
        </Body>
      </Tooltip.Target>
      <Tooltip.Content
        sx={{
          maxw: {
            default: '200px',
            medium: '370px',
            small: '300px'
          }
        }}
      >
        <b>These are not NFT’s and will not show up in your wallet. </b>
        We recommend buying in-game assets with Passport.
      </Tooltip.Content>
    </Tooltip>
  );
};

const Title = ({
  product,
  tradable,
  isGGoGCard,
  isGogToGgog
}: {
  product: Product;
  tradable: boolean;
  isGGoGCard: boolean;
  isGogToGgog: boolean;
}) => {
  const title = product.name.split('==========', 2);
  let name = '';
  if (!isGGoGCard) {
    name = product.name;
  } else {
    if (isGogToGgog) {
      name = title[0] || '';
    } else {
      name = title[1] || '';
    }
  }

  return (
    <Box sx={{ paddingBottom: 'base.spacing.x2', display: 'flex', alignItems: 'center' }}>
      <Heading size="small" sx={{ userSelect: 'none', mr: 'base.spacing.x2' }}>
        {name}
      </Heading>
      {!tradable && !isGGoGCard ? <NotTradableTag /> : undefined}
    </Box>
  );
};

const CurrencyDisplay = ({ name, size }: { name: string; size: BodyProps['size'] }) => {
  const symbol = name.toLowerCase().replace(/^t|w(\w+)$/gm, '$1');
  const symbolURL = `/images/currency--${symbol}.svg`;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center'
      }}
    >
      <Box
        rc={<Image width={16} height={16} src={symbolURL} alt={name} />}
        sx={{
          mr: 'base.spacing.x1'
        }}
      />
      <Body size={size} weight="regular" sx={{ pr: 'base.spacing.x1', color: 'inherit' }}>
        {name}
      </Body>
    </Box>
  );
};

const Price = ({
  price,
  markdownPrice,
  discounted,
  currency,
  highlighted
}: {
  price: number;
  markdownPrice: number;
  discounted: boolean;
  currency: string;
  highlighted: boolean;
}) => {
  const copySize: BodyProps['size'] = highlighted
    ? ['medium', 'medium', 'large', 'medium', 'large']
    : ['small', 'small', 'medium', 'small', 'medium'];
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          color: highlighted ? 'base.color.text.body.primary' : 'base.color.text.body.secondary'
        }}
      >
        <CurrencyDisplay name={currency} size={copySize} />
        <Body size={copySize} weight="bold" sx={{ color: 'inherit' }}>
          {toFormatedCurrency(price)}
        </Body>
        {discounted && (
          <Body
            sx={{
              px: 'base.spacing.x2',
              textDecoration: 'base.font.decoration.lineThrough',
              color: 'base.color.text.body.secondary'
            }}
            size={copySize}
          >
            &nbsp;{toFormatedCurrency(markdownPrice)}&nbsp;
          </Body>
        )}
      </Box>
    </Box>
  );
};

const Pricing = ({
  product,
  quantity,
  bundleSize
}: {
  product: Product;
  quantity: number;
  bundleSize: number;
}) => {
  const discounted = product.pricing.regularPrice !== product.pricing.price;

  const prices = [
    {
      price: product.pricing.price,
      currency: product.pricing.currency,
      regularPrice: product.pricing.regularPrice,
      salePrice: product.pricing.salePrice
    },
    ...(product.pricing.alternateCurrencies || [])
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start'
      }}
    >
      {prices.map(({ price: basePrice, currency, regularPrice, salePrice }, index) => {
        const comparisonPrice = Number(
          basePrice === salePrice ? regularPrice : salePrice || regularPrice
        );

        const price = Number(basePrice) * quantity * bundleSize;

        const markdownPrice = comparisonPrice * quantity * bundleSize;

        return (
          <Price
            key={currency}
            price={Number(price)}
            currency={currency}
            discounted={discounted}
            markdownPrice={markdownPrice}
            highlighted={index === 0}
          />
        );
      })}
    </Box>
  );
};

const StoreDiscount = ({
  product,
  bundleSize,
  discount = 0
}: {
  product: Product;
  bundleSize: number;
  discount: number;
}) => {
  const { salePrice, regularPrice } = product.pricing;
  const extraDiscount = +salePrice !== 0;
  const retailPrice = Number(regularPrice) * bundleSize;
  const groupDiscount = product?.pricing?.groupDiscountPercentage;
  const totalDiscount = Math.max(groupDiscount, discount);
  const isGroupDiscount = product.pricing?.isGroupDiscountApplied;
  const groupDiscountAvailable = product.pricing?.numberOfDiscountPurchaseLeft;
  if (!totalDiscount)
    return <Box sx={{ display: 'flex', width: '100%', mb: 'base.spacing.x10' }} />;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        justifyContent: 'flex-start',
        mb: !totalDiscount ? 'base.spacing.x10' : 'base.spacing.x3'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          px: 'base.spacing.x2',
          py: 'base.spacing.x1',
          brad: 'base.borderRadius.x1',
          bg: 'base.color.brand.1'
        }}
      >
        <Body
          size="small"
          weight="bold"
          sx={{
            color: 'base.color.text.body.inverse.primary'
          }}
        >
          {isGroupDiscount && totalDiscount === groupDiscount ? (
            <Tooltip>
              <Tooltip.Target>
                {extraDiscount && 'Extra'} {totalDiscount}% off applied | {groupDiscountAvailable}{' '}
                Remaining
              </Tooltip.Target>
              <Tooltip.Content>
                Your wallet has {groupDiscountAvailable} purchase
                {groupDiscountAvailable > 1 && 's'} with a discount
              </Tooltip.Content>{' '}
            </Tooltip>
          ) : (
            <>
              {extraDiscount && 'Extra'} {totalDiscount}% off applied
            </>
          )}
        </Body>
      </Box>

      {extraDiscount && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            px: 'base.spacing.x2',
            py: 'base.spacing.x1',
            brad: 'base.borderRadius.x1',
            bg: 'base.color.brand.1',
            ml: 'base.spacing.x2'
          }}
        >
          <Body
            size="small"
            weight="bold"
            sx={{
              color: 'base.color.text.body.inverse.primary'
            }}
          >
            RRP {toFormatedCurrency(retailPrice)}
          </Body>
        </Box>
      )}
    </Box>
  );
};

const QuantityInput = ({
  quantity,
  maxQuantity,
  setQuantity,
  bundleSize,
  bundleQuantity
}: {
  quantity: number;
  bundleSize: number;
  bundleQuantity: number;
  maxQuantity: number;
  setQuantity: (qty: number) => void; // eslint-disable-line no-unused-vars
}) => {
  const [inputQty, setInputQty] = useState(bundleQuantity);
  const [focused, setFocused] = useState(false);

  const maxQty = Math.floor(maxQuantity / (bundleSize || 1));

  useEffect(() => {
    setInputQty(bundleQuantity);
  }, [bundleQuantity]);

  if (!focused) {
    return (
      <Body
        size="medium"
        weight="bold"
        onClick={() => setFocused(true)}
        sx={{
          px: 'base.spacing.x2',
          cursor: 'pointer',
          mx: 'base.spacing.x1',
          '&:hover': {
            color: 'base.color.text.body.primary',
            borderRadius: 'base.borderRadius.x4',
            outlineStyle: 'solid',
            outlineColor: 'base.color.text.body.secondary',
            outlineWidth: 'base.border.size.100',
            outlineOffset: '-4px',
            bg: 'base.color.translucent.emphasis.300',
            py: 'base.spacing.x2'
          }
        }}
      >
        {bundleQuantity}
      </Body>
    );
  }

  const valid = inputQty !== bundleQuantity && inputQty > 0 && inputQty <= maxQty;

  const onDone = () => {
    setFocused(false);

    if (valid) {
      const newQty = inputQty * bundleSize;
      const deltaQty = Math.abs(quantity - newQty) * (bundleQuantity > inputQty ? -1 : 1);
      setQuantity(deltaQty);
    }
    setInputQty(inputQty);
  };

  return (
    <Body size="medium" weight="bold" sx={{ px: 'base.spacing.x2' }}>
      <TextInput
        autoFocus
        sizeVariant="medium"
        value={inputQty}
        hideClearValueButton
        onChange={(e) => {
          const value = Number(e.target.value);
          if (!isNaN(value) && value >= 0 && value <= maxQty) {
            setInputQty(value);
          }
        }}
        onBlur={onDone}
        sx={{ width: '100%' }}
      >
        <TextInput.StatefulButtCon
          icon={valid ? 'Tick' : 'Close'}
          state="initial"
          sx={{
            minWidth: 'auto',
            width: 'base.spacing.x6',
            height: 'base.spacing.x6'
          }}
        />
      </TextInput>
    </Body>
  );
};

const Quantity = ({
  // stockQty,
  quantity,
  bundleSize,
  setQuantity,
  maxQuantity,
  notAvailable,
  exceedQuantity
}: {
  product: Product;
  quantity: number;
  bundleSize: number;
  maxQuantity: number;
  notAvailable: boolean;
  exceedQuantity: boolean;
  setQuantity: (qty: number) => void; // eslint-disable-line no-unused-vars
}) => {
  // const { allowlisted } = useProductsContext();
  const bundleQuantity = Math.floor(quantity / (bundleSize || 1));
  // const willExceedMaxQuantity = quantity + bundleSize > maxQuantity;
  // const overMaxQuantity = bundleQuantity === 1 && (exceededQuantity || willExceedMaxQuantity);
  //const notAvailable = stockQty <= 0 || overMaxQuantity || !allowlisted;

  if (notAvailable) {
    return;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1 / 2,
        alignItems: 'center',
        justifyContent: 'space-between',
        marginRight: 'base.spacing.x4',
        marginBottom: 'base.spacing.x3',
        width: '125px'
      }}
    >
      <ButtCon icon="Minus" variant="tertiary" onClick={() => setQuantity(-1 * bundleSize)} />
      <QuantityInput
        quantity={quantity}
        bundleQuantity={bundleQuantity}
        setQuantity={setQuantity}
        bundleSize={bundleSize}
        maxQuantity={maxQuantity * bundleSize}
      />
      <ButtCon
        icon="Add"
        variant="tertiary"
        disabled={exceedQuantity || quantity >= maxQuantity * bundleSize}
        onClick={() => setQuantity(+1 * bundleSize)}
      />
    </Box>
  );
};

const htmlDescriptionStyles: BoxProps['sx'] = {
  a: {
    color: 'base.color.text.link.primary'
  },
  'a:hover': {
    textDecoration: 'base.font.decoration.underline'
  }
};

const HtmlDescription = ({ html }: { html: string }) => {
  return (
    <Box
      sx={{
        ...htmlDescriptionStyles,
        paddingBottom: 'base.spacing.x8',
        userSelect: 'none',
        flex: 'auto'
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const Description = ({
  product,
  isGGoGCard,
  isGogToGgog
}: {
  product: Product;
  isGGoGCard: boolean;
  isGogToGgog: boolean;
}) => {
  if (!isGGoGCard) {
    return <HtmlDescription html={product.description} />;
  } else {
    let htmlDesc = '';
    const desc = product.description.split('<p>==========</p>', 2);
    if (isGogToGgog) {
      htmlDesc = desc[0] || '';
    } else {
      htmlDesc = desc[1] || '';
    }
    return <HtmlDescription html={htmlDesc} />;
  }
};

const BuyNow = (props: {
  product: Product;
  onClick: () => void;
  stockQty: number;
  notAvailable: boolean;
  isLimited: boolean;
  disabledByAmount?: boolean;
}) => {
  const { stockQty, onClick, product, notAvailable, isLimited, disabledByAmount } = props;
  const [{ saleEnded }] = useStore();
  const { connectWallet, connected } = useConnectWidget();

  const inStock = product.stockStatus === StockStatusEnum.InStock;
  const outOfStock = stockQty <= 0 && !inStock;

  if (notAvailable) {
    let label = '';
    if (saleEnded) {
      label = 'Pre-sale has finished';
    }
    // else if (!allowlisted) {
    //   label = 'Sorry this item is unavailable';
    // }
    else if (outOfStock) {
      label = 'Sold out';
    }

    return (
      <Body
        weight="bold"
        size="medium"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          backgroundColor: 'base.color.translucent.emphasis.300',
          borderRadius: 'base.spacing.x1',
          px: 'base.spacing.x2',
          py: 'base.spacing.x4'
        }}
      >
        {label}
      </Body>
    );
  }

  const handleOnClick = () => {
    if (connected) {
      onClick();
      return;
    }

    connectWallet();
  };

  if (isLimited) {
    return (
      <Button
        sx={{
          flex: 1,
          whiteSpace: 'nowrap',
          backgroundColor: 'base.color.translucent.emphasis.300',
          marginBottom: 'base.spacing.x3'
        }}
        disabled
        onClick={handleOnClick}
      >
        Buy now
      </Button>
    );
  }

  return (
    <Button
      sx={{
        flex: 1,
        whiteSpace: 'nowrap',
        marginBottom: 'base.spacing.x3'
      }}
      disabled={disabledByAmount}
      onClick={handleOnClick}
    >
      Buy now
    </Button>
  );
};

const AvailableQuantity = ({
  availableQuantity,
  limitQuantity
}: {
  availableQuantity: string;
  limitQuantity: string;
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'flex-end'
      }}
    >
      <>
        Available {availableQuantity}/{limitQuantity}
      </>
    </Box>
  );
};

const CountdownRefreshTime = ({
  countdownTime,
  onRefresh
}: {
  countdownTime: string;
  onRefresh: () => void;
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'flex-end'
      }}
    >
      <>
        Refreshes in:&nbsp;
        <CountdownTimer countDownTime={countdownTime} onRefresh={onRefresh} />
      </>
    </Box>
  );
};

const WalletLimitInfomation = ({
  availableQuantity,
  limitQuantity,
  countdownTime,
  onRefresh
}: {
  availableQuantity?: string;
  limitQuantity?: string;
  countdownTime: string;
  onRefresh: () => void;
}) => {
  if (!countdownTime && !limitQuantity) {
    return null;
  }
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'end',
        justifyContent: 'end'
      }}
    >
      <Box sx={{ width: '125px', marginRight: 'base.spacing.x4' }} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flex: 1
        }}
      >
        {availableQuantity && limitQuantity ? (
          <AvailableQuantity availableQuantity={availableQuantity} limitQuantity={limitQuantity} />
        ) : null}
        {countdownTime ? (
          <>
            <CountdownRefreshTime countdownTime={countdownTime} onRefresh={onRefresh} />
          </>
        ) : null}
      </Box>
    </Box>
  );
};

const StepperNumberInput = ({
  value,
  onCommit,
  onClear,
  maxValue,
  step
}: {
  value: string;
  onCommit: any;
  onClear: any;
  maxValue?: number;
  step: number;
}) => {
  const clampTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [draftValue, setDraftValue] = useState('');

  useEffect(() => {
    if (value === '') {
      setDraftValue('');
      return;
    }
    const n = Number(value);
    if (!Number.isFinite(n)) {
      setDraftValue('');
      return;
    }
    setDraftValue(String(n));
  }, [value]);

  useEffect(() => {
    return () => {
      if (clampTimerRef.current) {
        clearTimeout(clampTimerRef.current);
        clampTimerRef.current = undefined;
      }
    };
  }, []);

  const commit = (rawDraft: string) => {
    if (rawDraft === '') {
      onClear();
      return;
    }

    const n = Number(rawDraft);
    if (!Number.isFinite(n)) {
      onCommit(0);
      return;
    }

    const maxByInventory =
      maxValue !== undefined && Number.isFinite(maxValue)
        ? Math.max(Math.floor(maxValue / step) * step, 0)
        : undefined;

    const intendedValue = Math.min(Math.max(n, 0), 10000000);
    const steppedValue = Math.floor(intendedValue / step) * step;
    const clampedValue =
      maxByInventory !== undefined
        ? Math.min(steppedValue, maxByInventory, 10000000)
        : Math.min(steppedValue, 10000000);

    onCommit(clampedValue);
    setDraftValue(String(clampedValue));
  };

  const handleStep = (delta: number) => {
    if (clampTimerRef.current) {
      clearTimeout(clampTimerRef.current);
      clampTimerRef.current = undefined;
    }

    const currentRaw = draftValue === '' ? value : draftValue;
    const current = Number(currentRaw);
    const safeCurrent = Number.isFinite(current) ? current : 0;
    const next = Math.max(safeCurrent + delta, 0);
    commit(String(next));
  };

  return (
    <div className={styles.stepper}>
      <input
        className={styles.stepInput}
        type="text"
        inputMode="numeric"
        value={draftValue}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw !== '' && !/^[0-9]+$/.test(raw)) {
            return;
          }
          setDraftValue(raw);

          if (clampTimerRef.current) {
            clearTimeout(clampTimerRef.current);
          }

          clampTimerRef.current = setTimeout(() => {
            commit(raw);
          }, 800);
        }}
        onBlur={() => {
          if (clampTimerRef.current) {
            clearTimeout(clampTimerRef.current);
            clampTimerRef.current = undefined;
          }
          commit(draftValue);
        }}
      />
      <div className={styles.stepperButtons}>
        <button
          type="button"
          className={styles.stepperButton}
          aria-label="Increase"
          onClick={() => handleStep(step)}
        >
          ▲
        </button>
        <button
          type="button"
          className={styles.stepperButton}
          aria-label="Decrease"
          onClick={() => handleStep(-step)}
        >
          ▼
        </button>
      </div>
    </div>
  );
};

const GoGInput = ({
  gogValue,
  setGogValue,
  setGGogValue,
  isGogToGgog,
  RATE,
  formatDisabledValue,
  maxValue
}: {
  gogValue: string;
  setGogValue: any;
  setGGogValue: any;
  isGogToGgog: boolean;
  RATE: number;
  formatDisabledValue?: boolean;
  maxValue?: number;
}) => {
  const isEditable = isGogToGgog;
  const shouldFormatDisabledValue = formatDisabledValue ?? true;
  let displayValue = gogValue;

  // Only format the value for display when this side is disabled (isGogToGgog)
  if (!isGogToGgog && shouldFormatDisabledValue) {
    const num = Number(gogValue || 0);
    if (Number.isNaN(num) || num === 0) {
      displayValue = '0';
    } else {
      displayValue = num.toFixed(2);
    }
  }
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center'
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '18px',
          height: '18px',
          marginRight: '4px',
          color: '#fff',
          fontWeight: 600,
          backgroundImage: "url('/images/currency--gog.svg')",
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          borderRadius: '50%'
        }}
      />
      {isEditable ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%'
          }}
        >
          <StepperNumberInput
            value={gogValue}
            maxValue={maxValue}
            step={10}
            onClear={() => {
              setGogValue('');
              setGGogValue('');
            }}
            onCommit={(clampedValue: number) => {
              setGogValue(String(clampedValue));
              setGGogValue(String(clampedValue * RATE));
            }}
          />
        </div>
      ) : (
        <input style={{ width: '100%' }} className={styles.input} value={displayValue} disabled />
      )}
    </div>
  );
};

const GGoGInput = ({
  gGogValue,
  setGGogValue,
  setGogValue,
  isGogToGgog,
  RATE,
  maxValue
}: {
  gGogValue: string;
  setGGogValue: any;
  setGogValue: any;
  isGogToGgog: boolean;
  RATE: number;
  maxValue?: number;
}) => {
  const isEditable = !isGogToGgog;
  let displayValue = gGogValue;

  // Only format the value for display when this side is disabled (!isGogToGgog)
  if (isGogToGgog) {
    const num = Number(gGogValue || 0);
    if (Number.isNaN(num) || num === 0) {
      displayValue = '0';
    } else {
      displayValue = new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0
      }).format(Math.floor(num));
    }
  }
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center'
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '18px',
          height: '18px',
          marginRight: '4px',
          color: '#fff',
          fontWeight: 600,
          backgroundImage: "url('/images/gGoG.png')",
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          borderRadius: '50%'
        }}
      />
      {isEditable ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%'
          }}
        >
          <StepperNumberInput
            value={gGogValue}
            maxValue={maxValue}
            step={10}
            onClear={() => {
              setGGogValue('');
              setGogValue('');
            }}
            onCommit={(clampedValue: number) => {
              const denom = RATE;
              setGGogValue(String(clampedValue));
              setGogValue(String(denom > 0 ? clampedValue / denom : Number.NaN));
            }}
          />
        </div>
      ) : (
        <input style={{ width: '100%' }} className={styles.input} value={displayValue} disabled />
      )}
    </div>
  );
};

const GGogExchangeItem = ({
  isGogToGgog,
  handleSwap,
  gGogValue,
  setGGogValue,
  RATE
}: {
  isGogToGgog: boolean;
  handleSwap: () => void;
  gGogValue: string;
  setGGogValue: any;
  RATE: number;
}) => {
  const [gogValue, setGogValue] = useState('');
  const inventoryClampTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [{ walletAddress, balanceRefreshToken }] = useStore();
  const previousBalanceRefreshTokenRef = useRef<number | undefined>(undefined);
  const [ggogInventoryBalance, setGgogInventoryBalance] = useState<string | undefined>(undefined);
  const [ggogInventoryLoading, setGgogInventoryLoading] = useState(false);
  const [gogInventoryBalance, setGogInventoryBalance] = useState<string | undefined>(undefined);
  const [gogInventoryLoading, setGogInventoryLoading] = useState(false);
  const [withdrawFeeGog, setWithdrawFeeGog] = useState<string | undefined>(undefined);
  const [withdrawFeeLoading, setWithdrawFeeLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!isGogToGgog) {
      setGogInventoryLoading(false);
      return;
    }

    if (!walletAddress) {
      setGogInventoryBalance(undefined);
      setGogInventoryLoading(false);
      return;
    }

    setGogInventoryLoading(true);
    const t = setTimeout(() => {
      (async () => {
        try {
          const res = await getCurrentUserGogBalance({ walletAddress });
          if (cancelled) return;
          setGogInventoryBalance(res.formattedBalance);
        } catch {
          if (cancelled) return;
          setGogInventoryBalance(undefined);
        } finally {
          if (cancelled) return;
          setGogInventoryLoading(false);
        }
      })();
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [isGogToGgog, walletAddress]);

  useEffect(() => {
    let cancelled = false;

    if (isGogToGgog) {
      setGgogInventoryLoading(false);
      return;
    }

    if (!walletAddress) {
      setGgogInventoryBalance(undefined);
      setGgogInventoryLoading(false);
      return;
    }

    (async () => {
      setGgogInventoryLoading(true);
      try {
        const res = await gGoGBalanceApi(walletAddress);
        if (cancelled) return;
        setGgogInventoryBalance(String(res.ggogBalance ?? 0));
      } catch {
        if (cancelled) return;
        setGgogInventoryBalance(undefined);
      } finally {
        if (cancelled) return;
        setGgogInventoryLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isGogToGgog, walletAddress]);

  useEffect(() => {
    let cancelled = false;

    if (!walletAddress) {
      return;
    }

    if (previousBalanceRefreshTokenRef.current === undefined) {
      previousBalanceRefreshTokenRef.current = balanceRefreshToken;
      return;
    }

    if (previousBalanceRefreshTokenRef.current === balanceRefreshToken) {
      return;
    }

    previousBalanceRefreshTokenRef.current = balanceRefreshToken;

    (async () => {
      setGogInventoryLoading(true);
      setGgogInventoryLoading(true);
      try {
        const [gogRes, ggogRes] = await Promise.allSettled([
          getCurrentUserGogBalance({ walletAddress, forceRefresh: true }),
          gGoGBalanceApi(walletAddress)
        ]);

        if (cancelled) return;

        if (gogRes.status === 'fulfilled') {
          setGogInventoryBalance(gogRes.value.formattedBalance);
        } else {
          setGogInventoryBalance(undefined);
        }

        if (ggogRes.status === 'fulfilled') {
          setGgogInventoryBalance(String(ggogRes.value.ggogBalance ?? 0));
        } else {
          setGgogInventoryBalance(undefined);
        }
      } finally {
        if (cancelled) return;
        setGogInventoryLoading(false);
        setGgogInventoryLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [balanceRefreshToken, walletAddress]);

  useEffect(() => {
    let cancelled = false;

    if (isGogToGgog) {
      setWithdrawFeeGog(undefined);
      setWithdrawFeeLoading(false);
      return;
    }

    const amount = Number(gGogValue);
    if (!Number.isFinite(amount) || amount <= 0) {
      setWithdrawFeeGog('0');
      setWithdrawFeeLoading(false);
      return;
    }

    setWithdrawFeeLoading(true);
    const t = setTimeout(() => {
      (async () => {
        try {
          const fee = await getGGoGWithdrawalFeeGog(amount);
          if (cancelled) return;
          setWithdrawFeeGog(fee ?? '0');
        } catch {
          if (cancelled) return;
          setWithdrawFeeGog('0');
        } finally {
          if (cancelled) return;
          setWithdrawFeeLoading(false);
        }
      })();
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [isGogToGgog, gGogValue]);

  const inventoryDisplayValue = isGogToGgog
    ? gogInventoryLoading
      ? '...'
      : gogInventoryBalance ?? '0'
    : ggogInventoryLoading
    ? '...'
    : ggogInventoryBalance ?? '0';

  const parseBalanceNumber = (s?: string) => {
    if (!s) return undefined;
    const n = Number(String(s).replace(/,/g, ''));
    return Number.isFinite(n) ? n : undefined;
  };

  const maxPayValue = isGogToGgog
    ? parseBalanceNumber(gogInventoryBalance)
    : parseBalanceNumber(ggogInventoryBalance);

  useEffect(() => {
    if (maxPayValue === undefined || !Number.isFinite(maxPayValue)) {
      if (inventoryClampTimerRef.current) {
        clearTimeout(inventoryClampTimerRef.current);
        inventoryClampTimerRef.current = undefined;
      }
      return;
    }

    const maxByInventory = Math.max(Math.floor(maxPayValue / 10) * 10, 0);

    if (isGogToGgog) {
      const current = Number(gogValue);
      if (!Number.isFinite(current) || current <= 0) return;
      if (current <= maxByInventory) {
        if (inventoryClampTimerRef.current) {
          clearTimeout(inventoryClampTimerRef.current);
          inventoryClampTimerRef.current = undefined;
        }
        return;
      }

      if (inventoryClampTimerRef.current) {
        clearTimeout(inventoryClampTimerRef.current);
      }

      inventoryClampTimerRef.current = setTimeout(() => {
        setGogValue(String(maxByInventory));
        setGGogValue(String(maxByInventory * RATE));
      }, 800);
      return;
    }

    const current = Number(gGogValue);
    if (!Number.isFinite(current) || current <= 0) return;
    if (current <= maxByInventory) {
      if (inventoryClampTimerRef.current) {
        clearTimeout(inventoryClampTimerRef.current);
        inventoryClampTimerRef.current = undefined;
      }
      return;
    }

    if (inventoryClampTimerRef.current) {
      clearTimeout(inventoryClampTimerRef.current);
    }

    inventoryClampTimerRef.current = setTimeout(() => {
      setGGogValue(String(maxByInventory));
      setGogValue(String(RATE > 0 ? maxByInventory / RATE : Number.NaN));
    }, 800);
  }, [RATE, gGogValue, gogValue, isGogToGgog, maxPayValue, setGGogValue]);

  useEffect(() => {
    return () => {
      if (inventoryClampTimerRef.current) {
        clearTimeout(inventoryClampTimerRef.current);
        inventoryClampTimerRef.current = undefined;
      }
    };
  }, []);

  const inventoryDisplayValueFormatted = (() => {
    if (inventoryDisplayValue === '...') return '...';
    const n = Number(inventoryDisplayValue);
    if (!Number.isFinite(n)) return inventoryDisplayValue;
    if (!isGogToGgog) {
      return String(Math.floor(n));
    }

    return (Math.floor(n * 100) / 100).toFixed(2);
  })();

  const ggogToGetGrossNumber = Number(gGogValue);
  const ggogToGetGross = Number.isFinite(ggogToGetGrossNumber) ? ggogToGetGrossNumber : 0;
  const ggogToGetNet = ggogToGetGross > 0 ? ggogToGetGross * 0.98 : 0;
  const ggogToGetFee = ggogToGetGross > 0 ? ggogToGetGross * 0.02 : 0;
  const ggogToGetNetText = (() => {
    const net = Math.floor(ggogToGetNet);
    if (!Number.isFinite(net) || net <= 0) return '0';
    return String(net);
  })();
  const ggogToGetFeeText = (() => {
    const feeFloor2 = Math.floor(ggogToGetFee * 100) / 100;
    if (!Number.isFinite(feeFloor2) || feeFloor2 <= 0) return '0';
    if (Number.isInteger(feeFloor2)) return String(feeFloor2);
    return feeFloor2.toFixed(2);
  })();

  const gogToGetGross = ggogToGetGross > 0 ? ggogToGetGross / RATE : 0;
  const withdrawFeeGogNumber = withdrawFeeGog ? Number(withdrawFeeGog) : 0;
  const withdrawFeeGogSafe = Number.isFinite(withdrawFeeGogNumber) ? withdrawFeeGogNumber : 0;
  const gogToGetNet = Math.max(gogToGetGross - withdrawFeeGogSafe, 0);
  const gogToGetNetText = String(Number.isFinite(gogToGetNet) ? gogToGetNet : 0);
  const gogToGetDisplayText =
    ggogToGetGross <= 0 ? '0' : withdrawFeeLoading ? '...' : gogToGetNetText;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        width: '100%'
      }}
    >
      <div style={{ width: '100%' }}>
        <div className={styles.to}>
          <div className={styles.toCol}>
            <div>I want to convert</div>
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <div className={styles.convert} style={{ flex: 1, width: '100%' }}>
                {isGogToGgog ? (
                  <GoGInput
                    gogValue={gogValue}
                    setGogValue={setGogValue}
                    setGGogValue={setGGogValue}
                    isGogToGgog={isGogToGgog}
                    RATE={RATE}
                    maxValue={maxPayValue}
                  />
                ) : (
                  <GGoGInput
                    gGogValue={gGogValue}
                    setGGogValue={setGGogValue}
                    setGogValue={setGogValue}
                    isGogToGgog={isGogToGgog}
                    RATE={RATE}
                    maxValue={maxPayValue}
                  />
                )}
              </div>
            </div>
          </div>
          <div className={styles.toDivider} aria-hidden />
          <div className={styles.toCol}>
            <div>In inventory</div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '2px',
                lineHeight: 1,
                backgroundColor: '#000',
                color: '#fff'
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '18px',
                  height: '18px',
                  marginRight: '4px',
                  color: '#fff',
                  fontWeight: 600,
                  backgroundImage: isGogToGgog
                    ? "url('/images/currency--gog.svg')"
                    : "url('/images/gGoG.png')",
                  backgroundSize: 'cover',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  borderRadius: '50%'
                }}
              />
              <input
                className={styles.input}
                type="text"
                style={{ width: '100%' }}
                value={inventoryDisplayValueFormatted}
                disabled
              />
            </div>
          </div>
        </div>
      </div>

      {/* <div style={{ width: '100%', marginTop: '6px', marginBottom: '6px' }}>
        {isGogToGgog ? (
          <p style={{ margin: 0 }}>{gogValue || '0'} $GoG will be converted to gGoG</p>
        ) : (
          <p style={{ margin: 0 }}>{gGogValue || '0'} gGoG will be converted to $GoG</p>
        )}
      </div> */}

      <div
        style={{
          position: 'relative',
          top: '10px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%'
        }}
      >
        <div
          style={{
            width: '24px',
            height: '24px',
            backgroundImage: "url('/images/icon_swap.png?v=2')",
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            borderRadius: '50%',
            cursor: 'pointer'
          }}
          onClick={handleSwap}
        ></div>
      </div>

      <div style={{ width: '100%' }}>
        <div>
          <span>To get</span>
          <div
            style={{
              padding: '2px',
              backgroundColor: '#000',
              color: '#fff',
              width: '46%'
            }}
          >
            {isGogToGgog ? (
              <GGoGInput
                gGogValue={ggogToGetNetText}
                setGGogValue={setGGogValue}
                setGogValue={setGogValue}
                isGogToGgog={isGogToGgog}
                RATE={RATE}
              />
            ) : (
              <GoGInput
                gogValue={
                  withdrawFeeLoading ? '...' : cutDecimal(Number(gogToGetDisplayText), 2) + ''
                }
                setGogValue={setGogValue}
                setGGogValue={setGGogValue}
                isGogToGgog={isGogToGgog}
                RATE={RATE}
                formatDisabledValue={false}
              />
            )}
          </div>
        </div>
      </div>

      {isGogToGgog ? (
        <div style={{ width: '100%', marginTop: '6px', marginBottom: '6px' }}>
          <p style={{ margin: 0 }}>
            {Number(ggogToGetNetText) / 10 || '0'} $GoG will be converted to{' '}
            {Number(ggogToGetNetText)} gGoG.
          </p>
          <p style={{ margin: 0 }}>{ggogToGetFeeText} gGoG will be subtracted as fee.</p>
        </div>
      ) : (
        <div style={{ width: '100%', marginTop: '6px', marginBottom: '6px' }}>
          {withdrawFeeLoading ? (
            <>
              <p style={{ margin: 0 }}>...</p>
              <p style={{ margin: 0 }}>...</p>
            </>
          ) : (
            <>
              <p style={{ margin: 0 }}>
                {(Number(gogToGetDisplayText) * 10).toFixed(2) || '0'} gGoG will be converted to{' '}
                {cutDecimal(Number(gogToGetDisplayText), 2)} $GoG.
              </p>
              <p style={{ margin: 0 }}>
                {Number(withdrawFeeGog).toFixed(2) || '0'} $GoG will be subtracted as fee.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const ProductCard = ({ product, onClick }: ProductCardProps) => {
  const { maxQuantity, bundleSize, stockQty, attributes } = getProductAttributes(product);
  const [productQty, setProductQty, exceedQuantity, resetQuantity] = useQuantity(
    bundleSize,
    maxQuantity
  );
  const RATE = 10;
  const [{ walletAddress: storeWalletAddress, saleEnded }, dispatch] = useStore();
  const { setHasDiscount, hasDiscount } = useProductsContext();
  const [progress, setProgress] = useState<number>(0);
  const [discount, setDiscount] = useState<number>(0);
  const [showDiscount, setShowdiscount] = useState<boolean>(false);

  const bondingCurve = product.pricing?.bondingCurve;
  const discountCurve = product.pricing?.discountCurve;
  const inStock = product.stockStatus === StockStatusEnum.InStock;
  const outOfStock = stockQty <= 0 && !inStock;
  const notAvailable = outOfStock || saleEnded;

  const [isLimited, setIsLimited] = useState<boolean>(false);
  const [available, setAvailable] = useState<string>('');
  const [limit, setLimit] = useState<string>('');
  const [countdown, setCountdown] = useState('');

  const availableBundle = available ? parseInt(available) / bundleSize : null;
  const limitBundle = limit ? parseInt(limit) / bundleSize : null;
  const { openConfirmModal } = useConfirmModal();

  const refreshCountdown = () => {
    setIsLimited(false);
    setCountdown(product?.walletLimit?.countdownTime || `${24 * 60 * 60}`);
    setAvailable(product?.walletLimit?.availableQuantity || limit);
  };

  useEffect(() => {
    setIsLimited(product?.walletLimit?.isLimited);
    setAvailable(product?.walletLimit?.availableQuantity);
    setLimit(product?.walletLimit?.limitQuantity);
    setCountdown(product?.walletLimit?.countdownTime);
    resetQuantity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  useEffect(() => {
    if (!bondingCurve) {
      return;
    }
    const remainingDiscountPool = bondingCurve?.remainingDiscountPool ?? 0;
    const percentage = product.pricing?.totalDiscountPercentage ?? 0;

    if (bondingCurve) {
      const currentProgress = Number(remainingDiscountPool);

      if (currentProgress >= 0 && currentProgress <= 100) {
        setProgress(Number(currentProgress.toFixed(2)));
      }

      setDiscount(percentage ?? 0);
    }
  }, [bondingCurve, product, setHasDiscount, hasDiscount]);

  useEffect(() => {
    if (!discountCurve) {
      return;
    }
    setHasDiscount(true);
    const remainingDiscountPool = discountCurve?.remainingDiscountPool ?? 0;
    const percentage = product.pricing?.totalDiscountPercentage ?? 0;
    const currentProgress = Number(remainingDiscountPool);

    if (currentProgress >= 0 && currentProgress <= 100) {
      setProgress(Number(currentProgress.toFixed(2)));
    }

    setDiscount(percentage ?? 0);
  }, [discountCurve, product.pricing?.totalDiscountPercentage, setHasDiscount]);

  useEffect(() => {
    setShowdiscount(!notAvailable && (bondingCurve?.active || !!discountCurve));
  }, [notAvailable, bondingCurve, discountCurve]);

  const tradable = product.collectionType !== ProductCollectionType.WEB2;

  const isGGoGCard = product.slug === 'gogggog-test' || product.slug === 'gogggog';
  const [direction, setDirection] = useState<'gogToGgog' | 'ggogToGog'>('gogToGgog');

  const isGogToGgog = direction === 'gogToGgog';
  const [gGogAmount, setGGogAmount] = useState('');

  const gGogAmountNumber = Number(gGogAmount);
  const isZeroOrInvalidAmount =
    isGGoGCard && (!gGogAmount || Number.isNaN(gGogAmountNumber) || gGogAmountNumber === 0);

  const minGGogAmount = 100;
  const isBelowMinPayAmount =
    isGGoGCard &&
    isGogToGgog &&
    minGGogAmount !== undefined &&
    Number.isFinite(gGogAmountNumber) &&
    gGogAmountNumber > 0 &&
    gGogAmountNumber < minGGogAmount;

  const handleSwap = () => {
    setDirection(isGogToGgog ? 'ggogToGog' : 'gogToGgog');
  };

  const handleConvertGGoG2GoGConfirm = async (amount: number) => {
    const feeGog = await getGGoGWithdrawalFeeGog(amount);

    const feeGogNumber = feeGog ? Number(feeGog) : Number.NaN;
    const netGog = Number.isFinite(feeGogNumber) ? amount / 10 - feeGogNumber : undefined;
    const netGogText = netGog !== undefined ? String(netGog) : 'unable to compute';

    openConfirmModal({
      title: 'Confirm Conversion',
      description:
        `Are you sure you want to convert ${amount} gGoG to $GoG?\n\n` +
        `You will receive: ${netGogText} $GOG\n\n` +
        `This conversion process usually takes 2 working days to complete.`,
      confirmLabel: 'Confirm',
      cancelLabel: 'Cancel',
      onConfirm: () => handleConvertGGoG2GoG(amount)
    });
  };

  const handleConvertGGoG2GoG = (amount: number) => {
    if (storeWalletAddress) {
      dispatch({ payload: { type: 'SET_TOGGLE_MODAL_OPEN', open: true } });
      gGoG2GoGApi({
        walletAddress: storeWalletAddress,
        amount
      })
        .then((res) => {
          if (res.success) {
            dispatch({ payload: { type: 'INCREMENT_BALANCE_REFRESH_TOKEN' } });
            openConfirmModal({
              description: res.message,
              showConfirmButton: false,
              cancelLabel: 'Close'
            });
          } else {
            openConfirmModal({
              description: res.message,
              showConfirmButton: false,
              cancelLabel: 'Close'
            });
          }
        })
        .catch((err) => {
          openConfirmModal({
            description: err.message,
            showConfirmButton: false,
            cancelLabel: 'Close'
          });
        });
    } else {
      openConfirmModal({
        description: `Please connect your wallet first.`,
        showConfirmButton: false,
        cancelLabel: 'Close'
      });
    }
  };

  return (
    <CardFrame attributes={attributes}>
      <Card
        rc={<div />}
        sx={{
          cursor: 'auto',
          aspectRatio: 'initial',
          '& .textContainer': {
            position: 'initial',
            flex: 'auto'
          },
          '.innerTextContainer': {
            flex: 'auto'
          },
          height: '100%'
        }}
      >
        {product.image?.sourceUrl && (
          <Card.AssetImage
            sx={{
              pointerEvents: 'none',
              userSelect: 'none',
              aspectRatio: '9 / 5',
              minHeight: `${hasDiscount ? '180px !important' : ''}`,
              // maxHeight: `${hasDiscount ? '200px !important' : ''}`,
              width: '100%'
            }}
            aspectRatio="9:5"
            objectFit="cover"
            responsiveSizes={[640, 860, 1440] as ResponsiveSizes}
            relativeImageSizeInLayout={''}
            imageUrl={product.image.sourceUrl}
          />
        )}
        <Card.Caption
          sx={{
            position: 'absolute',
            top: 'base.spacing.x4',
            right: 'base.spacing.x4'
          }}
        >
          {isGGoGCard ? null : <StockStatus product={product} stockQty={stockQty} />}
        </Card.Caption>
        <Card.Title>
          <Title
            product={product}
            tradable={tradable}
            isGGoGCard={isGGoGCard}
            isGogToGgog={isGogToGgog}
          />
        </Card.Title>

        <Card.Description
          sx={{
            flex: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Description product={product} isGGoGCard={isGGoGCard} isGogToGgog={isGogToGgog} />
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              flexDirection: 'column',
              alignItems: 'flex-start',
              marginBottom: 'base.spacing.x4'
            }}
          >
            <StoreDiscount product={product} bundleSize={bundleSize} discount={discount} />
            {!isGGoGCard ? (
              <Pricing
                product={product}
                quantity={Math.floor(productQty / bundleSize)}
                bundleSize={bundleSize}
              />
            ) : (
              <GGogExchangeItem
                isGogToGgog={isGogToGgog}
                handleSwap={handleSwap}
                gGogValue={gGogAmount}
                setGGogValue={setGGogAmount}
                RATE={RATE}
              />
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              flexDirection: 'row',
              justifyContent: 'flex-start',
              alignItems: 'flex-end'
            }}
          >
            {!isGGoGCard ? (
              <Quantity
                product={product}
                quantity={productQty}
                setQuantity={setProductQty}
                maxQuantity={availableBundle ? availableBundle : maxQuantity}
                notAvailable={notAvailable}
                bundleSize={bundleSize}
                exceedQuantity={exceedQuantity}
              />
            ) : null}

            {!isGGoGCard || isGogToGgog ? (
              <BuyNow
                product={product}
                stockQty={stockQty}
                onClick={() => {
                  const base = Number(gGogAmount) / RATE;
                  const needToPay = Number(base.toFixed(2));
                  const gogToGgogBuyNowAmount = Math.round(needToPay * 10);
                  onClick(
                    product,
                    isGGoGCard && isGogToGgog ? gogToGgogBuyNowAmount : productQty,
                    isGGoGCard && isGogToGgog ? 'gog' : undefined
                  );
                }}
                notAvailable={notAvailable}
                isLimited={isLimited}
                disabledByAmount={
                  isGGoGCard && isGogToGgog
                    ? isZeroOrInvalidAmount || isBelowMinPayAmount
                    : undefined
                }
              />
            ) : (
              <Button
                sx={{
                  flex: 1,
                  whiteSpace: 'nowrap',
                  backgroundColor: 'base.color.translucent.emphasis.300',
                  marginBottom: 'base.spacing.x3'
                }}
                disabled={isZeroOrInvalidAmount}
                onClick={() => handleConvertGGoG2GoGConfirm(Number(gGogAmount || 0))}
              >
                Convert
              </Button>
            )}
          </Box>
          {!notAvailable ? (
            <WalletLimitInfomation
              onRefresh={refreshCountdown}
              limitQuantity={limitBundle != null ? `${limitBundle}` : undefined}
              availableQuantity={availableBundle != null ? `${availableBundle}` : undefined}
              countdownTime={countdown}
            />
          ) : null}
          {showDiscount ? (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: 'base.spacing.x1'
                }}
              >
                Discount pool remaining
                <Box sx={{ color: 'base.color.accent.1' }}>{showDiscount ? progress : 0}%</Box>
              </Box>
              <Box>
                <ProgressBar
                  colorVariant="gradient"
                  variant="determinate"
                  size={'medium'}
                  value={progress}
                  sx={{
                    gap: '0',
                    width: '100%',
                    borderRadius: 'base.borderRadius.x5',
                    marginTop: 'base.spacing.x2',
                    backgroundColor: 'transparent',
                    border: '1px solid white'
                  }}
                >
                  <ProgressBar.Label text=""></ProgressBar.Label>
                  <ProgressBar.Caption></ProgressBar.Caption>
                </ProgressBar>
              </Box>
            </Box>
          ) : null}
        </Card.Description>
      </Card>
    </CardFrame>
  );
};
